import { useEffect } from 'react';

/**
 * Contrat de la barre d'actions du coin bas-droit.
 *
 * Séparé de `CornerDock.jsx` : un module qui exporte à la fois des composants et
 * des constantes casse le rafraîchissement à chaud de Vite.
 */

export const CORNER_DOCK_ID = 'orbit-corner-dock';

/**
 * Ordres d'affichage EXPLICITES. Le conteneur est ancré en bas : un ordre PLUS
 * GRAND place le bouton plus bas, donc plus près du coin et du pouce.
 *
 * Déclarés ici, et non au cas par cas, pour que la disposition ne dépende PAS de
 * l'ordre de montage des composants — lequel change avec la route.
 */
export const DOCK_ORDER = {
  // Sur une page-modèle, deux raccourcis d'édition coexistent et ne mènent pas
  // au même endroit : le CONTENU affiché (l'instance) d'abord — c'est ce que
  // l'on vient corriger neuf fois sur dix —, le GABARIT ensuite.
  builderContent: 5,
  builderPublic: 10,
  taskActivity: 20,
  builderChat: 30,
  report: 40,
};

/**
 * Plancher des ordres attribués automatiquement : au-delà de tous les ordres
 * explicites, pour qu'un emplacement sans ordre ne s'intercale jamais au milieu
 * d'une disposition voulue.
 */
export const ALPHA_BASE = 100;

/**
 * Repli ALPHABÉTIQUE pour les emplacements sans ordre déclaré.
 *
 * Sans lui, tous partageraient le même ordre et se rangeraient dans l'ordre de
 * MONTAGE — donc au petit bonheur de la route. Un classement par nom est
 * arbitraire mais STABLE et prévisible, ce qui est tout ce qu'on demande à un
 * repli : ne jamais bouger d'une page à l'autre.
 *
 * Registre au niveau du MODULE et non un contexte React : la barre est montée à
 * côté de l'application, pas au-dessus, et n'est donc pas un ancêtre des
 * emplacements.
 */
const noms = new Set();
const abonnes = new Set();
let tri = [];

function recalculer() {
  // Référence remplacée seulement quand le classement change : `useSyncExternalStore`
  // exige un instantané stable, sinon il boucle.
  tri = [...noms].sort((a, b) => a.localeCompare(b, 'fr'));
  abonnes.forEach((prevenir) => prevenir());
}

export function souscrireAuTri(prevenir) {
  abonnes.add(prevenir);
  return () => abonnes.delete(prevenir);
}

export function lireTri() {
  return tri;
}

/** Inscrit un nom au classement alphabétique ; rend sa fonction de retrait. */
export function inscrireAuTri(nom) {
  noms.add(nom);
  recalculer();
  return () => { noms.delete(nom); recalculer(); };
}

/** Ordre effectif d'un emplacement : le sien s'il en a un, sinon son rang. */
export function ordreEffectif(order, nom, classement) {
  if (order != null) return order;
  const rang = classement.indexOf(nom);
  return ALPHA_BASE + (rang === -1 ? classement.length : rang);
}

/**
 * Décalage de la barre quand un panneau latéral DROIT est ouvert.
 *
 * Sans ça, les boutons se posent sur le panneau — typiquement sur le champ de
 * saisie de l'assistant, qui vit en bas à droite. Les décaler plutôt que les
 * déplacer à gauche : la gauche porte la navigation, et changer de côté selon
 * l'état d'un panneau ferait bouger un repère que l'utilisateur a mémorisé.
 *
 * Le décalage passe par une VARIABLE CSS et non par une prop React : la barre
 * n'est pas un ancêtre des panneaux, et le panneau de l'assistant est
 * REDIMENSIONNABLE à la souris — une valeur figée serait fausse dès le premier
 * glissement.
 *
 * Registre indexé pour supporter plusieurs panneaux : on retient le PLUS LARGE
 * des panneaux ouverts, seul capable de recouvrir la barre.
 */
const panneaux = new Map();
let decalage = 0;
let reserve = 0;

/** La plus grande largeur parmi les panneaux qui satisfont `filtre`. */
const plusLarge = (filtre) => {
  let max = 0;
  for (const p of panneaux.values()) if (filtre(p) && p.largeur > max) max = p.largeur;
  return max;
};

const poser = (nom, valeur) => {
  if (typeof document === 'undefined') return;
  document.documentElement.style.setProperty(nom, `${valeur}px`);
};

function recalculerDecalage() {
  const large = plusLarge(() => true);
  if (large !== decalage) {
    decalage = large;
    poser('--orbit-dock-inset', decalage);
  }
  const pousse = plusLarge(p => p.pousse);
  if (pousse !== reserve) {
    reserve = pousse;
    poser('--orbit-panneau-droit', reserve);
  }
}

/**
 * Déclare un panneau droit ouvert ; rend sa fonction de retrait.
 *
 * `pousse` distingue les deux façons d'occuper le bord droit :
 * - par défaut le panneau RECOUVRE — il flotte au-dessus de l'application, qui
 *   ne sait pas qu'il est là ;
 * - `pousse: true` en fait une COLONNE — l'application se rétrécit d'autant
 *   (`--orbit-panneau-droit`, consommée par `#orbit_front_app` dans index.css),
 *   et on continue de voir et d'utiliser ce qui était dessous.
 * Les deux variables restent distinctes : la barre du coin doit s'écarter dans
 * les DEUX cas, la réservation de place seulement dans le second.
 */
export function declarerPanneauDroit(id, largeur, { pousse = false } = {}) {
  panneaux.set(id, { largeur: Number(largeur) || 0, pousse });
  recalculerDecalage();
  return () => { panneaux.delete(id); recalculerDecalage(); };
}

/**
 * La largeur RÉELLEMENT disponible pour l'application, panneaux compris.
 *
 * `window.innerWidth` a cessé de la décrire le jour où un panneau droit a pris
 * de la place au lieu d'en recouvrir : tout ce qui se cale au bord droit du
 * viewport — menus, popovers, panneaux ancrés — atterrissait alors SOUS la
 * colonne. Le bord à ne pas dépasser se lit ici, jamais sur `innerWidth`.
 */
export function largeurUtile() {
  if (typeof window === 'undefined') return 0;
  return Math.max(0, window.innerWidth - reserve);
}

/**
 * À poser dans tout panneau ancré à droite qui pourrait recouvrir la barre.
 * `largeur` peut changer à chaque rendu (panneau redimensionnable) : le décalage
 * suit.
 */
export function usePanneauDroit(id, ouvert, largeur, { pousse = false } = {}) {
  useEffect(() => {
    if (!ouvert) return undefined;
    return declarerPanneauDroit(id, largeur, { pousse });
  }, [id, ouvert, largeur, pousse]);
}
